import { useState, useEffect } from 'react';
import { useLocalStorage } from './useLocalStorage';

/**
 * Custom hook for managing workout state
 * @param {Array} allExercises - All available exercises for enrichment
 * @returns {Object} - Workout state and handlers
 */
export const useWorkout = (allExercises) => {
  // Core workout state
  const [workout, setWorkout] = useLocalStorage("workout", []);
  const [workoutName, setWorkoutName] = useLocalStorage("workoutName", "", false);
  const [currentStep, setCurrentStep] = useLocalStorage("currentStep", 0);
  const [customWorkoutData, setCustomWorkoutData] = useState(null);
  const [fromLibrary, setFromLibrary] = useLocalStorage("fromLibrary", false);

  // Ensure workout exercises have full data including repRange
  useEffect(() => {
    if (workout.length > 0) {
      const updatedWorkout = workout.map(exercise => {
        const fullExercise = allExercises.find(ex => ex.id === exercise.id);
        return fullExercise || exercise;
      });
      
      // Only update if there are changes
      const hasChanges = updatedWorkout.some((ex, index) => 
        JSON.stringify(ex) !== JSON.stringify(workout[index])
      );
      
      if (hasChanges) {
        setWorkout(updatedWorkout);
      }
    }
  }, [workout, allExercises, setWorkout]);

  // Process custom workout data when workout page loads
  useEffect(() => {
    const isCustomWorkoutFlag = localStorage.getItem('isCustomWorkout');
    const customWorkoutFromStorage = localStorage.getItem('customWorkout');
    
    if (isCustomWorkoutFlag === 'true' && customWorkoutFromStorage) {
      try {
        const customWorkout = JSON.parse(customWorkoutFromStorage);
        const enrichedExercises = customWorkout.exercises.map(exercise => {
          const fullExercise = allExercises.find(ex => ex.id === exercise.id);
          return fullExercise || exercise;
        });
        
        setCustomWorkoutData(enrichedExercises);
        setWorkoutName(customWorkout.name);
        setWorkout(enrichedExercises);
        setCurrentStep(0);
        
        // Clear custom workout data and flag
        localStorage.removeItem('customWorkout');
        localStorage.removeItem('isCustomWorkout');
      } catch (error) {
        console.error('Error processing custom workout:', error);
      }
    }
  }, [allExercises, setWorkout, setWorkoutName, setCurrentStep]);

  // Workout navigation handlers
  const handleNext = (navigate, setIsExploding) => {
    const currentWorkout = customWorkoutData || workout;
    if (currentStep < currentWorkout.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Clear custom workout data when completing
      if (customWorkoutData) {
        setCustomWorkoutData(null);
        setWorkout(currentWorkout);
      }
      setIsExploding(true);
      navigate("/complete");
      setTimeout(() => setIsExploding(false), 4000);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleRestart = () => {
    setWorkout([]);
    setWorkoutName("");
    setCurrentStep(0);
    setCustomWorkoutData(null);
    setFromLibrary(false);
  };

  const handleRepeatWorkout = (workoutData) => {
    setWorkout(workoutData.exercises);
    setWorkoutName(workoutData.name);
    setCurrentStep(0);
    setFromLibrary(true);
  };

  return {
    // State
    workout,
    setWorkout,
    workoutName,
    setWorkoutName,
    currentStep,
    setCurrentStep,
    customWorkoutData,
    setCustomWorkoutData,
    fromLibrary,
    setFromLibrary,
    
    // Handlers
    handleNext,
    handlePrevious,
    handleRestart,
    handleRepeatWorkout
  };
};

export default useWorkout;
